// src/context/TenantContext.js
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import tenantService from '../services/tenantService';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) throw new Error('useTenant must be used within TenantProvider');
  return context;
};

// Default theme values when no tenant is configured
const DEFAULT_THEME = {
  primary: '#1976d2',
  primaryDark: '#1565c0',
  primaryLight: '#42a5f5',
  secondary: '#4CAF50',
  navBackground: '#333333',
  navText: '#ffffff',
  navActiveIndicator: '#4CAF50'
};

const DEFAULT_BRANDING = {
  companyName: 'Chemical Monitor',
  supportEmail: '',
  showPoweredBy: true,
  logoUrl: ''  // Path to tenant logo (e.g., '/logos/synergy.png')
};

const DEFAULT_FEATURES = {
  chemicalMonitoring: true,
  washPackages: true,
  barrelAlerts: true,
  facilityInventory: true,
  dencarIntegration: false,
  billing: true,
  developerConsole: false
};

// Default navigation config - all items visible with default labels
const DEFAULT_NAVIGATION = {
  hiddenItems: [],  // Array of paths to hide (e.g., ['/wash-packages', '/barrel-inventory'])
  customLabels: {},  // Object mapping paths to custom labels (e.g., { '/': 'Dashboard' })
  defaultPage: '/home'  // Page to redirect to after login (landing page for all tenants)
};

export const TenantProvider = ({ children }) => {
  const { organization, currentUser } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load tenant when organization changes
  // IMPORTANT: Depend on organization?.id to ensure we reload when switching between
  // organizations, even if they have the same tenantId. Also depend on currentUser?.uid
  // to ensure we reload when the user changes (logout/login with different user).
  useEffect(() => {
    const loadTenant = async () => {
      // If no user or no organization, use defaults
      if (!currentUser || !organization) {
        setTenant(null);
        setLoading(false);
        return;
      }

      // If organization has no tenantId, use defaults
      if (!organization.tenantId) {

        setTenant(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const tenantData = await tenantService.getCurrentTenant();
        setTenant(tenantData);
        setError(null);

      } catch (err) {
        console.error('[TenantContext] Failed to load tenant:', err);
        setError(err.message);
        setTenant(null);
      } finally {
        setLoading(false);
      }
    };

    loadTenant();
  }, [organization?.id, organization?.tenantId, currentUser?.uid]);

  // Memoize derived values to prevent unnecessary recalculations
  const theme = useMemo(() => tenant?.theme || DEFAULT_THEME, [tenant?.theme]);

  const branding = useMemo(() => ({
    ...DEFAULT_BRANDING,
    ...tenant?.branding
  }), [tenant?.branding]);

  const features = useMemo(() => ({
    ...DEFAULT_FEATURES,
    ...tenant?.features
  }), [tenant?.features]);

  const navigation = useMemo(() => ({
    ...DEFAULT_NAVIGATION,
    ...tenant?.navigation
  }), [tenant?.navigation]);

  // Helper to check if a feature is enabled for the current tenant
  const hasFeature = useCallback((featureName) => {
    return features[featureName] === true;
  }, [features]);

  // Get the display name for the app
  const appName = branding.companyName || 'Chemical Monitor';

  // Get the logo URL
  const logoUrl = branding.logoUrl || '';

  // Memoize the colors object
  const colors = useMemo(() => ({
    primary: theme.primary,
    primaryDark: theme.primaryDark,
    primaryLight: theme.primaryLight,
    secondary: theme.secondary,
    navBackground: theme.navBackground,
    navText: theme.navText,
    navActiveIndicator: theme.navActiveIndicator
  }), [theme]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    tenant,
    loading,
    error,
    theme,
    branding,
    features,
    navigation,
    hasFeature,
    appName,
    logoUrl,
    colors
  }), [tenant, loading, error, theme, branding, features, navigation, hasFeature, appName, logoUrl, colors]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
